"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import {
  AlertTriangle,
  Check,
  Loader2,
  Lock,
  Upload,
  X,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { ScoreGauge } from "@/components/cv-form/score-gauge";
import { cn } from "@/lib/utils";
import { createClient } from "@/lib/supabase/client";
import { trackEvent } from "@/lib/analytics";
import {
  fileHasPdfSignature,
  isAcceptedClientPdfFile,
  PDF_FILE_ACCEPT,
} from "@/lib/files/pdf";
import { analyzeResume, extractPdfText } from "@/lib/cv/ats-check";
import type { AtsCheckResult, ParsedResume } from "@/lib/cv/ats-check";
import { AtsParsedTable } from "@/components/landing/ats-parsed-table";
import {
  clearAtsCheckDraft,
  loadAtsCheckDraft,
  saveAtsCheckDraft,
} from "@/components/landing/ats-check-draft";
import {
  OAUTH_NEXT_COOKIE_MAX_AGE_SECONDS,
  OAUTH_NEXT_COOKIE_NAME,
} from "@/lib/auth/oauth-constants";
import { SubmitCvReviewError } from "@/lib/cv/submit-cv-review";
import {
  classifyUploadError,
  markReviewStarted,
} from "@/lib/cv/review-funnel-instrumentation";

const MAX_FILE_BYTES = 10 * 1024 * 1024;

// ID label -> the exact experienceLevel enum value /api/cv expects.
const EXPERIENCE_OPTIONS: { value: string; label: string }[] = [
  {
    value: "Fresh Graduate without Internship Experience",
    label: "Fresh graduate (tanpa pengalaman magang)",
  },
  {
    value: "Fresh Graduate with Internship Experience",
    label: "Fresh graduate (ada pengalaman magang)",
  },
  { value: "0-1 Years of Professional Experience", label: "0-1 tahun kerja" },
  { value: "1-3 Years of Professional Experience", label: "1-3 tahun kerja" },
  { value: "3-5 Years of Professional Experience", label: "3-5 tahun kerja" },
  { value: "5-10 Years of Professional Experience", label: "5-10 tahun kerja" },
];

type Status = "idle" | "parsing" | "done" | "error";

export function AtsCheckTool({ locale }: { locale: string }) {
  const router = useRouter();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const levelSelectRef = useRef<HTMLSelectElement>(null);
  const resumedRef = useRef(false);

  const [file, setFile] = useState<File | null>(null);
  const [experienceLevel, setExperienceLevel] = useState("");
  const [result, setResult] = useState<AtsCheckResult | null>(null);
  const [parsed, setParsed] = useState<ParsedResume | null>(null);
  const [status, setStatus] = useState<Status>("idle");
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const cekCvPath = `/${locale}/cek-cv-ats`;

  const submitForRealReview = useCallback(
    async (pdf: File, level: string) => {
      setIsSubmitting(true);
      setErrorMsg(null);
      // Which pipeline step failed decides whether the error counts as an
      // upload failure or a review (pipeline) failure.
      let stage: "upload" | "review" = "upload";
      try {
        const formData = new FormData();
        formData.append("file", pdf);
        formData.append("experienceLevel", level);
        formData.append("reviewPurpose", "job");
        formData.append("allowTalentPool", "false");

        const cvResponse = await fetch("/api/cv", {
          method: "POST",
          body: formData,
        });
        const cvPayload = await cvResponse.json().catch(() => null);
        if (!cvResponse.ok || !cvPayload?.id) {
          throw new SubmitCvReviewError(
            cvPayload?.error ?? "Gagal mengunggah CV",
            cvResponse.status,
          );
        }

        trackEvent("cv_upload_success", { source: "ats" });
        stage = "review";

        const cvId: string = cvPayload.id;
        const reviewResponse = await fetch("/api/review", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ cvId }),
        });
        const reviewPayload = await reviewResponse.json().catch(() => null);
        if (!reviewResponse.ok) {
          throw new SubmitCvReviewError(
            reviewPayload?.error ?? "Gagal memulai review",
            reviewResponse.status,
          );
        }

        // The review page resolves `[id]` as the review-request id, not the cvId.
        const reviewRequestId: string | undefined =
          reviewPayload?.reviewRequest?.id ?? reviewPayload?.id;
        if (!reviewRequestId) {
          throw new SubmitCvReviewError("Review request tidak terbuat");
        }

        trackEvent("review_started", { source: "ats", level });
        markReviewStarted();
        clearAtsCheckDraft();
        router.push(`/${locale}/dashboard/review/${reviewRequestId}`);
      } catch (error) {
        console.error("Auto review submit failed", error);
        trackEvent(stage === "upload" ? "cv_upload_failed" : "review_failed", {
          source: "ats",
          error_class: classifyUploadError(error),
        });
        setErrorMsg(
          "Skor kamu sudah terbuka, tapi review lengkap gagal dimulai. Coba lagi dari dashboard.",
        );
        setIsSubmitting(false);
      }
    },
    [locale, router],
  );

  // On mount: restore any draft and resume after an OAuth round-trip.
  useEffect(() => {
    let mounted = true;
    const supabase = createClient();

    const init = async () => {
      const draft = loadAtsCheckDraft();
      if (draft && mounted) {
        if (draft.result) setResult(draft.result);
        if (draft.parsed) setParsed(draft.parsed);
        if (draft.experienceLevel) setExperienceLevel(draft.experienceLevel);
        if (draft.cvFile) setFile(draft.cvFile);
        if (draft.result) setStatus("done");
      }

      let authed = false;
      try {
        const {
          data: { user },
        } = await supabase.auth.getUser();
        authed = Boolean(user);
      } catch {
        authed = false;
      }
      if (!mounted) return;
      setIsAuthenticated(authed);

      // Returning signed-in user with a pending draft -> reveal + auto-start the
      // real AI review. Guard against React strict-mode double invoke.
      if (
        authed &&
        draft?.result &&
        draft.cvFile &&
        draft.experienceLevel &&
        !resumedRef.current
      ) {
        resumedRef.current = true;
        void submitForRealReview(draft.cvFile, draft.experienceLevel);
      }
    };

    void init();
    return () => {
      mounted = false;
    };
  }, [submitForRealReview]);

  const runCheck = useCallback(async (pdf: File) => {
    setStatus("parsing");
    setErrorMsg(null);
    try {
      const extracted = await extractPdfText(pdf);
      const { result: checkResult, parsed: parsedResume } =
        analyzeResume(extracted);
      setResult(checkResult);
      setParsed(parsedResume);
      setStatus("done");
      return { checkResult, parsed: parsedResume };
    } catch (error) {
      console.error("ATS check parse failed", error);
      setErrorMsg("Gagal membaca PDF. Pastikan file tidak rusak dan coba lagi.");
      setStatus("error");
      return null;
    }
  }, []);

  const handleFile = useCallback(
    async (selected: File | null) => {
      if (!selected) return;
      setErrorMsg(null);

      if (!isAcceptedClientPdfFile(selected)) {
        setErrorMsg("Format harus PDF. Export CV kamu sebagai PDF dulu.");
        return;
      }
      if (selected.size > MAX_FILE_BYTES) {
        setErrorMsg("Ukuran file maksimal 10MB.");
        return;
      }
      if (!(await fileHasPdfSignature(selected))) {
        setErrorMsg("File ini bukan PDF yang valid.");
        return;
      }

      setFile(selected);
      const checked = await runCheck(selected);
      if (checked) {
        void saveAtsCheckDraft({
          cvFile: selected,
          experienceLevel,
          result: checked.checkResult,
          parsed: checked.parsed,
        });
      }
    },
    [experienceLevel, runCheck],
  );

  const handleUnlock = useCallback(async () => {
    if (!file || !result) return;
    if (!experienceLevel) {
      setErrorMsg("Pilih level pengalaman kamu dulu.");
      // The level select sits far above the CTA: bring it into view and
      // focus it so the click visibly does something.
      const select = levelSelectRef.current;
      if (select) {
        if (typeof select.scrollIntoView === "function") {
          select.scrollIntoView({ behavior: "smooth", block: "center" });
        }
        select.focus({ preventScroll: true });
      }
      return;
    }

    await saveAtsCheckDraft({ cvFile: file, experienceLevel, result, parsed });

    if (isAuthenticated) {
      void submitForRealReview(file, experienceLevel);
      return;
    }

    const supabase = createClient();
    const secureCookie =
      window.location.protocol === "https:" ? "; secure" : "";
    document.cookie = `${OAUTH_NEXT_COOKIE_NAME}=${encodeURIComponent(
      cekCvPath,
    )}; path=/; max-age=${OAUTH_NEXT_COOKIE_MAX_AGE_SECONDS}; samesite=lax${secureCookie}`;
    const redirectTo = `${window.location.origin}/auth/oauth?next=${encodeURIComponent(
      cekCvPath,
    )}`;
    trackEvent("oauth_start", { source: "ats", locale });
    const { error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: { redirectTo },
    });
    if (error) {
      router.push(`/${locale}/sign-up?redirect=${encodeURIComponent(cekCvPath)}`);
    }
  }, [
    cekCvPath,
    experienceLevel,
    file,
    isAuthenticated,
    locale,
    result,
    parsed,
    router,
    submitForRealReview,
  ]);

  const unlocked = isAuthenticated;
  const visibleChecks = result?.checks.filter((c) => c.id !== "readable") ?? [];

  return (
    <div className="rounded-2xl border border-border bg-card p-5 shadow-sm sm:p-8">
      <h2 className="text-2xl font-bold tracking-tight">
        Cek apakah CV kamu terbaca ATS
      </h2>
      <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
        Upload CV PDF. Kami cek apakah format-nya kebaca mesin ATS, langsung di
        browser kamu, file tidak dikirim ke server. Ini cek <strong>format</strong>,
        bukan penilaian isi. Review isi + rewrite dari AI kamu dapat setelah sign in.
      </p>

      <div className="mt-5 grid gap-3 sm:grid-cols-[1fr_auto] sm:items-end">
        <label className="block text-sm">
          <span className="mb-1.5 block font-medium">Level pengalaman</span>
          <select
            ref={levelSelectRef}
            value={experienceLevel}
            onChange={(event) => setExperienceLevel(event.target.value)}
            className="h-11 w-full rounded-lg border border-input bg-background px-3 text-sm"
          >
            <option value="">Pilih level...</option>
            {EXPERIENCE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>

        <Button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={status === "parsing" || isSubmitting}
          className="h-11 w-full sm:w-auto"
        >
          {status === "parsing" ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <Upload className="mr-2 h-4 w-4" />
          )}
          {file ? "Ganti CV (PDF)" : "Upload CV (PDF)"}
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          accept={PDF_FILE_ACCEPT}
          className="hidden"
          onChange={(event) => {
            void handleFile(event.target.files?.[0] ?? null);
            event.target.value = "";
          }}
        />
      </div>

      {file ? (
        <p className="mt-2 text-xs text-muted-foreground">File: {file.name}</p>
      ) : null}

      <p className="mt-2 text-xs text-muted-foreground">
        {"CV masih di Word? Save As > PDF dulu, lalu upload di sini."}
      </p>

      {errorMsg ? (
        <p className="mt-3 text-sm text-destructive">{errorMsg}</p>
      ) : null}

      {status === "parsing" ? (
        <div className="mt-6 flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          <span>Membaca CV kamu...</span>
        </div>
      ) : null}

      {result && status === "done" ? (
        <div className="mt-6">
          {result.isImageOnly ? (
            <div className="flex items-start gap-3 rounded-xl border border-destructive/40 bg-destructive/5 p-4">
              <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-destructive" />
              <div>
                <p className="font-medium text-destructive">
                  CV kamu tidak terbaca ATS
                </p>
                <p className="mt-1 text-sm text-muted-foreground">
                  {result.checks[0]?.detail}
                </p>
              </div>
            </div>
          ) : (
            <div className="grid gap-6 sm:grid-cols-[auto_1fr] sm:items-center">
              {/* Score: blurred + locked until sign-in */}
              <div className="relative mx-auto">
                <div
                  className={cn(
                    "transition",
                    !unlocked && "pointer-events-none select-none blur-[10px]",
                  )}
                  aria-hidden={!unlocked}
                >
                  <ScoreGauge score={result.score} label="Skor Keterbacaan ATS" />
                </div>
                {!unlocked ? (
                  <div className="absolute inset-0 flex flex-col items-center justify-center gap-1 text-center">
                    <Lock className="h-7 w-7 text-foreground/70" />
                    <span className="text-xs font-medium text-muted-foreground">
                      Sign in buat lihat skor
                    </span>
                  </div>
                ) : null}
              </div>

              {/* Checklist: always visible (the helpful teaser) */}
              <ul className="space-y-2">
                {visibleChecks.map((check) => (
                  <li key={check.id} className="flex items-start gap-2 text-sm">
                    {check.pass ? (
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-green-600" />
                    ) : (
                      <X className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
                    )}
                    <span>
                      <span className="font-medium">{check.label}.</span>{" "}
                      <span className="text-muted-foreground">{check.detail}</span>
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {!result.isImageOnly && parsed ? (
            <div className="mt-6">
              <AtsParsedTable resume={parsed} />
            </div>
          ) : null}

          <div className="mt-6 rounded-xl border bg-muted/40 p-4">
            {isSubmitting ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>Memulai review lengkap dari AI...</span>
              </div>
            ) : (
              <>
                <p className="text-sm font-medium">
                  {unlocked
                    ? "Lanjut ke review konten lengkap + rewrite dari AI Hyred."
                    : "Sign in gratis buat buka skor kamu + langsung dapat review konten + rewrite dari AI."}
                </p>
                <Button
                  type="button"
                  onClick={handleUnlock}
                  disabled={isSubmitting}
                  className="mt-3 h-11 w-full sm:w-auto"
                >
                  {unlocked ? null : <Lock className="mr-2 h-4 w-4" />}
                  {unlocked
                    ? "Mulai review lengkap AI"
                    : "Sign in & buka skor (gratis)"}
                </Button>
                {errorMsg ? (
                  <p
                    data-testid="ats-unlock-error"
                    className="mt-3 text-sm text-destructive"
                  >
                    {errorMsg}
                  </p>
                ) : null}
              </>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}
